import {useContext, useEffect, useRef, useState} from 'react'

import cx from 'classnames'
import PropTypes from 'prop-types'

import Context from '@s-ui/react-context'
import ListFilters from '@adv-mt/mt-list-filters'
import NavigationLastAdsSearches, {
  NavigationLastAdsSearchesModal,
  useLastAdsSearches
} from '@adv-mt/mt-navigation-last-ads-searches'
import NotificationAi from '@adv-mt/mt-notifications-ai'
import SearchAiQuickFilters, {useAiSearch} from '@adv-mt/mt-search-ai-quick-filters'
import SearchSave, {searchSaveSizes, searchSaveTrackingOrigins} from '@adv-mt/mt-search-save'
import SearchTags, {useSearchTags} from '@adv-mt/mt-search-tags'

import {
  DEFAULT_SEARCH_USE_CASE,
  DESIGNS,
  LIST_FILTERS_PLACES_DICTIONARY,
  PLACES,
  SEARCH_TAGS_PLACES_DICTIONARY,
  SEARCH_USE_CASE_BY_PLACE
} from './config.js'

export default function SearchSidebar({
  aggregations,
  createdAlert,
  dealerSlug,
  design = DESIGNS.DEFAULT,
  filtersConfig,
  filtersProps,
  header: ExtendedHeader,
  isFullWidth = false,
  isLoading,
  listingSectionId,
  overlayContentRef,
  pageName,
  place,
  search,
  searchSaveTrackingOrigin,
  showSearchSaveButton = false,
  withSearchTags = false,
  withLastSavedSearches = false,
  withAiQuickFilters = false
}) {
  const {domain, i18n} = useContext(Context)
  const searchTags = useSearchTags({pageName, search})

  const {lastAdsSearches, isLastAdsSearchesModalOpen, onCloseLastAdsSearchesModal, onOpenLastAdsSearchesModal} =
    useLastAdsSearches()

  const {
    isLoading: isLoadingAiSearch,
    onSearch: onSearchAi,
    notificationType,
    onCloseNotification
  } = useAiSearch({
    isLoading,
    listingSectionId,
    pageName
  })

  const baseClassNames = cx('mt-SearchSidebar', {
    'mt-SearchSidebar--fullWidth': isFullWidth
  })

  const searchSidebarFiltersClassNames = cx('mt-SearchSidebar-filters', {
    'mt-SearchSidebar-filters--wrapped': design === DESIGNS.WRAPPED
  })

  const hasFiltersApplied = Boolean(searchTags.length)

  const sentinelRef = useRef(null)
  const [isStickyActive, setIsStickyActive] = useState(false)

  useEffect(() => {
    if (!sentinelRef.current) return

    const observer = new IntersectionObserver(([entry]) => setIsStickyActive(entry.boundingClientRect.top < 0), {
      threshold: 0,
      rootMargin: '0px'
    })

    observer.observe(sentinelRef.current)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    setIsStickyActive(false)
  }, [search])

  const handleOnFilter = ({search, lastFilter}) => {
    domain
      .get(
        // Progressive rollout form new get url params search use case
        SEARCH_USE_CASE_BY_PLACE[place] || DEFAULT_SEARCH_USE_CASE
      )
      .execute({dealerSlug, lastFilter, listingSectionId, pageName, search})
  }

  return (
    <aside className={baseClassNames}>
      {showSearchSaveButton && (
        <div className="mt-SearchSidebar-createAlert">
          <SearchSave
            createdAlert={createdAlert}
            search={search}
            showTooltip={false}
            trackingOrigin={searchSaveTrackingOrigin}
            size={searchSaveSizes.LARGE}
          />
        </div>
      )}
      {Boolean(search) && (
        <>
          {withSearchTags && hasFiltersApplied && (
            <div className="mt-SearchSidebar-tags">
              <SearchTags
                dealerSlug={dealerSlug}
                listingSectionId={listingSectionId}
                onRemoveTag={handleOnFilter}
                pageName={pageName}
                place={SEARCH_TAGS_PLACES_DICTIONARY[place]}
                search={search}
              />
            </div>
          )}
          {withLastSavedSearches && (
            <NavigationLastAdsSearches lastAdsSearches={lastAdsSearches} onOpenModal={onOpenLastAdsSearchesModal} />
          )}

          {withAiQuickFilters && (
            <div
              className={cx('mt-SearchSidebar-quickFilters', {
                'mt-SearchSidebar-quickFilters--sticky': isStickyActive
              })}
            >
              <SearchAiQuickFilters isLoading={isLoadingAiSearch} onSearch={onSearchAi} isDefaultOpen />
            </div>
          )}

          <div className={searchSidebarFiltersClassNames}>
            <div className="mt-SearchSidebar-filtersHeader">
              <p className="mt-SearchSidebar-filtersHeaderInfo">{i18n.t('COMMON.SEARCH_AUTO_CLOSE_INFORMATION')}</p>
              {Boolean(ExtendedHeader) && (
                <div className="mt-SearchSidebar-filtersHeaderExtended">{ExtendedHeader}</div>
              )}
            </div>
            <ListFilters
              aggregations={aggregations}
              config={filtersConfig}
              filtersProps={filtersProps}
              isLoading={isLoading}
              isSearchSidebar
              listingSectionId={listingSectionId}
              onFilter={handleOnFilter}
              overlayContentRef={overlayContentRef}
              pageName={pageName}
              place={LIST_FILTERS_PLACES_DICTIONARY[place]}
              search={search}
            />
          </div>
        </>
      )}
      <div ref={sentinelRef} className="mt-SearchSidebar-sentinel" />

      {Boolean(notificationType) && !isLoadingAiSearch && (
        <NotificationAi onClose={onCloseNotification} type={notificationType} />
      )}

      {withLastSavedSearches && (
        <NavigationLastAdsSearchesModal
          isOpen={isLastAdsSearchesModalOpen}
          onClose={onCloseLastAdsSearchesModal}
          onOpen={onOpenLastAdsSearchesModal}
          lastAdsSearches={lastAdsSearches}
        />
      )}
    </aside>
  )
}

SearchSidebar.displayName = 'SearchSidebar'
SearchSidebar.propTypes = {
  aggregations: PropTypes.array,
  createdAlert: PropTypes.object,
  dealerSlug: PropTypes.string,
  design: PropTypes.string,
  filtersConfig: PropTypes.array,
  filtersProps: PropTypes.object,
  header: PropTypes.node,
  isFullWidth: PropTypes.bool,
  isLoading: PropTypes.bool,
  listingSectionId: PropTypes.string,
  overlayContentRef: PropTypes.node,
  pageName: PropTypes.string,
  place: PropTypes.string,
  search: PropTypes.object.isRequired,
  searchSaveTrackingOrigin: PropTypes.string,
  showSearchSaveButton: PropTypes.bool,
  withLastSavedSearches: PropTypes.bool,
  withSearchTags: PropTypes.bool,
  withAiQuickFilters: PropTypes.bool
}

export {searchSaveTrackingOrigins, DESIGNS as searchSidebarDesigns, PLACES as searchSidebarPlaces}
